// Global Types
import type {
  $Translate,
} from 'common.language';
import type {
  $Model,
  $ModelUpsertParams,
} from 'common.data';

// Global Enums
import {
  adminPages,
  adminSections,
} from 'common.enums';

// Global Components
import * as React from 'react';
import {
  Button,
  Card,
  Table,
} from 'reactstrap';
import {
  DeleteDialog,
  FormDialog,
  Loader,
  SectionHeader,
} from '@vroskus/library-components';

// Helpers
import {
  cleanFormValues,
} from '@vroskus/library-helpers/dist/browser';
import getUnicodeFlagIcon from 'country-flag-icons/unicode';
import {
  defaultDeleteItem,
  defaultGetData,
  defaultUpsertItem,
} from '../../../helpers';

// Components
import Page from '../../layout/Page';
import EmptyTable from '../../common/EmptyTable';
import RegionFields from '../../fields/RegionFields';
import ThQty from '../../common/ThQty';

// Services
import Services from '../../../services';

// Types
import type {
  $RedirectAction,
} from '../../../redux/modules/main/actions';

export type $OwnProps = $Translate;

export type $StateProps = Record<string, never>;

export type $DispatchProps = {
  redirect: $RedirectAction;
};

type $Props = $DispatchProps & $OwnProps & $StateProps;

type $State = {
  data: Array<$Model<'Region'>>;
  loading: boolean;
};

const pageId = adminPages.adminRegionsPage;

class AdminRegionsPageView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      data: [],
      loading: false,
    };
  }

  componentDidMount(): void {
    this.getData();
    this.setupSocket();
  }

  setupSocket(): void {
    Services.Socket.on({
      action: (data) => {
        if (data.modelName === 'Region') {
          this.getData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    await defaultGetData({
      action: async () => {
        await this.setStateAsync({
          loading: true,
        });

        const data = await Services.Api.getManyAdminRegion();

        await this.setStateAsync({
          data,
          loading: false,
        });
      },
      errorAction: async () => this.setStateAsync({
        loading: false,
      }),
    });
  }

  renderHeader() {
    const {
      t,
    } = this.props;

    const newItem: $ModelUpsertParams<'Region'> = {
      DateFormat: '',
      Domain: '',
      Flag: '',
      Name: '',
      RegistrationLetters: '',
      ShortDateFormat: '',
      TimeFormat: '',
      Timezone: '',
    };

    return (
      <Card id={adminSections[pageId].header}>
        <SectionHeader
          rightButtons={(
            <FormDialog
              fields={(
                <RegionFields item={newItem} />
              )}
              id={'create-region-dialog'}
              labelCloseButton={t('Close')}
              labelSaveButton={t('Save')}
              onAction={(values) => defaultUpsertItem({
                action: async () => {
                  const params = cleanFormValues<$Model<'Region'>>({
                    ...newItem,
                    ...values,
                  });

                  await Services.Api.upsertAdminRegion(params);
                },
              })}
              subTitle={t('New')}
              title={t('Region')}
            >
              <Button
                color={'info'}
                id={'create-region-button'}
                size={'sm'}
                title={t('Add new region')}
              >
                {t('Add new')}
              </Button>
            </FormDialog>
          )}
          title={t('Regions')}
        />
      </Card>
    );
  }

  renderButtons(dataItem: $Model<'Region'>) {
    const {
      redirect,
      t,
    } = this.props;

    const {
      id,
    } = dataItem;

    return (
      <div className={'d-flex justify-content-end'}>
        <Button
          className={'mr-1'}
          color={'secondary'}
          id={'redirect-admin-region-seasons-page-button'}
          onClick={() => redirect({
            pathname: `/admin/region/${dataItem.id}/seasons`,
          })}
          size={'sm'}
        >
          {t('Region seasons')}
        </Button>
        <FormDialog
          fields={(
            <RegionFields item={dataItem} />
          )}
          id={`update-region-dialog-${id}`}
          labelCloseButton={t('Close')}
          labelSaveButton={t('Save')}
          onAction={(values) => defaultUpsertItem({
            action: async () => {
              const params = cleanFormValues<$Model<'Region'>>({
                ...dataItem,
                ...values,
              });

              await Services.Api.upsertAdminRegion(params);
            },
          })}
          subTitle={dataItem.Name}
          title={t('Region')}
        >
          <Button
            className={'mr-1'}
            color={'warning'}
            id={`update-region-button-${id}`}
            outline
            size={'sm'}
          >
            {t('Edit')}
          </Button>
        </FormDialog>
        <DeleteDialog
          content={t('Do you really want to delete?')}
          id={`delete-region-dialog-${id}`}
          labelCloseButton={t('Close')}
          labelDeleteButton={t('Delete')}
          onAction={() => defaultDeleteItem({
            action: async () => Services.Api.deleteAdminRegion(dataItem),
          })}
          subTitle={dataItem.Name}
          title={t('Region')}
        >
          <Button
            color={'danger'}
            id={`delete-region-button-${id}`}
            outline
            size={'sm'}
          >
            {t('Delete')}
          </Button>
        </DeleteDialog>
      </div>
    );
  }

  renderTable() {
    const {
      t,
    } = this.props;
    const {
      data,
    } = this.state;

    return data.length === 0 ? (
      <EmptyTable className={'mt-5'} />
    ) : (
      <Table responsive>
        <thead>
          <tr>
            <th>{t('Flag')}</th>
            <th>{t('Name')}</th>
            <th>{t('Domain')}</th>
            <th>{t('Timezone')}</th>
            <th>{t('Date formats')}</th>
            <ThQty value={data.length} />
          </tr>
        </thead>
        <tbody>
          {data.map((dataItem: $Model<'Region'>) => (
            <tr key={dataItem.id}>
              <td>{getUnicodeFlagIcon(dataItem.Flag)}</td>
              <td>{dataItem.Name}</td>
              <td>{`${dataItem.Domain} / ${dataItem.RegistrationLetters}`}</td>
              <td>{dataItem.Timezone}</td>
              <td>
                <span>{dataItem.DateFormat}</span>
                <span className={'text-muted'}>{' | '}</span>
                <span>{dataItem.TimeFormat}</span>
                <span className={'text-muted'}>{' | '}</span>
                <span>{dataItem.ShortDateFormat}</span>
              </td>
              <td>
                {this.renderButtons(dataItem)}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }

  renderContent() {
    const {
      loading,
    } = this.state;

    return (
      <Card id={adminSections[pageId].list}>
        <Loader
          loading={loading === true}
          overlay
        >
          {this.renderTable()}
        </Loader>
      </Card>
    );
  }

  render() {
    return (
      <Page id={pageId}>
        <>
          {this.renderHeader()}
          {this.renderContent()}
        </>
      </Page>
    );
  }
}

export default AdminRegionsPageView;
